import React, { Suspense, useMemo } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { PrivateRoutes } from "./routing/PrivateRoutes";
import { OpenRoutes } from "./routing/OpenRoutes";
import LoadingSpinner from "./components/LoadingSpinner";
import ErrorBoundary from "./components/ErrorBoundary";
import "./utils/chunkErrorHandler";
import { lazyRetry } from "./utils/lazyRetry";

// Lazy load components with retry mechanism
const Layout = lazyRetry(() => import("./components/Layout"), "Layout");
const Home = lazyRetry(() => import("./pages/Home"), "Home");
const About = lazyRetry(() => import("./pages/About"), "About");
const Contact = lazyRetry(() => import("./pages/Contact"), "Contact");
const OurStore = lazyRetry(() => import("./pages/OurStore"), "OurStore");
const Blog = lazyRetry(() => import("./pages/Blog"), "Blog");
const Wishlist = lazyRetry(() => import("./pages/Wishlist"), "Wishlist");
const Login = lazyRetry(() => import("./pages/Login"), "Login");
const Forgotpassword = lazyRetry(() => import("./pages/Forgotpassword"), "Forgotpassword");
const Signup = lazyRetry(() => import("./pages/Signup"), "Signup");
const Resetpassword = lazyRetry(() => import("./pages/Resetpassword"), "Resetpassword");
const SingleBlog = lazyRetry(() => import("./pages/SingleBlog"), "SingleBlog");
const PrivacyPolicy = lazyRetry(() => import("./pages/PrivacyPolicy"), "PrivacyPolicy");
const RefundPloicy = lazyRetry(() => import("./pages/RefundPloicy"), "RefundPloicy");
const ShippingPolicy = lazyRetry(() => import("./pages/ShippingPolicy"), "ShippingPolicy");
const TermAndContions = lazyRetry(() => import("./pages/TermAndContions"), "TermAndContions");
const SingleProduct = lazyRetry(() => import("./pages/SingleProduct"), "SingleProduct");
const Cart = lazyRetry(() => import("./pages/Cart"), "Cart");
const Checkout = lazyRetry(() => import("./pages/Checkout"), "Checkout");
const Payment = lazyRetry(() => import("./pages/Payment"), "Payment");
const PaymentSuccess = lazyRetry(() => import("./pages/PaymentSuccess"), "PaymentSuccess");
const PaymentFailure = lazyRetry(() => import("./pages/PaymentFailure"), "PaymentFailure");
const OrderSuccess = lazyRetry(() => import("./pages/OrderSuccess"), "OrderSuccess");
const Orders = lazyRetry(() => import("./pages/Orders"), "Orders");
const Profile = lazyRetry(() => import("./pages/Profile"), "Profile");
const NotFound = lazyRetry(() => import("./pages/NotFound"), "NotFound");

function App() {
  // Memoize the layout element to prevent unnecessary re-renders
  const layoutElement = useMemo(() => (
    <Suspense fallback={<LoadingSpinner />}>
      <Layout />
    </Suspense>
  ), []);

  return (
    <BrowserRouter>
      <ErrorBoundary>
        <Routes>
          <Route path="/" element={layoutElement}>
            <Route index element={<Suspense fallback={null}><Home /></Suspense>} />
            <Route path="about" element={<Suspense fallback={<LoadingSpinner />}><About /></Suspense>} />
            <Route path="contact" element={<Suspense fallback={<LoadingSpinner />}><Contact /></Suspense>} />
            <Route path="product" element={<Suspense fallback={<LoadingSpinner />}><OurStore /></Suspense>} />
            <Route path="product/:id" element={<Suspense fallback={<LoadingSpinner />}><SingleProduct /></Suspense>} />
            <Route path="blogs" element={<Suspense fallback={<LoadingSpinner />}><Blog /></Suspense>} />
            <Route path="blog/:id" element={<Suspense fallback={<LoadingSpinner />}><SingleBlog /></Suspense>} />
            <Route path="cart" element={<Suspense fallback={<LoadingSpinner />}><Cart /></Suspense>} />
            <Route
              path="my-orders"
              element={
                <PrivateRoutes>
                  <Suspense fallback={<LoadingSpinner />}>
                    <Orders />
                  </Suspense>
                </PrivateRoutes>
              }
            />
            <Route
              path="my-profile"
              element={
                <PrivateRoutes>
                  <Suspense fallback={<LoadingSpinner />}>
                    <Profile />
                  </Suspense>
                </PrivateRoutes>
              }
            />
            <Route
              path="checkout"
              element={
                <PrivateRoutes>
                  <Suspense fallback={<LoadingSpinner />}>
                    <Checkout />
                  </Suspense>
                </PrivateRoutes>
              }
            />
            <Route
              path="payment"
              element={
                <PrivateRoutes>
                  <Suspense fallback={<LoadingSpinner />}>
                    <Payment />
                  </Suspense>
                </PrivateRoutes>
              }
            />
            <Route
              path="payment-success"
              element={
                <PrivateRoutes>
                  <Suspense fallback={<LoadingSpinner />}>
                    <PaymentSuccess />
                  </Suspense>
                </PrivateRoutes>
              }
            />
            <Route
              path="payment-failure"
              element={
                <PrivateRoutes>
                  <Suspense fallback={<LoadingSpinner />}>
                    <PaymentFailure />
                  </Suspense>
                </PrivateRoutes>
              }
            />
            <Route
              path="order-success"
              element={
                <PrivateRoutes>
                  <Suspense fallback={<LoadingSpinner />}>
                    <OrderSuccess />
                  </Suspense>
                </PrivateRoutes>
              }
            />
            <Route path="compare-product" element={<Suspense fallback={<LoadingSpinner />}><OurStore /></Suspense>} />
            <Route
              path="wishlist"
              element={
                <PrivateRoutes>
                  <Suspense fallback={<LoadingSpinner />}>
                    <Wishlist />
                  </Suspense>
                </PrivateRoutes>
              }
            />
            <Route
              path="login"
              element={
                <OpenRoutes>
                  <Suspense fallback={<LoadingSpinner />}>
                    <Login />
                  </Suspense>
                </OpenRoutes>
              }
            />
            <Route
              path="forgot-password"
              element={
                <OpenRoutes>
                  <Suspense fallback={<LoadingSpinner />}>
                    <Forgotpassword />
                  </Suspense>
                </OpenRoutes>
              }
            />
            <Route
              path="signup"
              element={
                <OpenRoutes>
                  <Suspense fallback={<LoadingSpinner />}>
                    <Signup />
                  </Suspense>
                </OpenRoutes>
              }
            />
            <Route path="reset-password/:token" element={<Suspense fallback={<LoadingSpinner />}><Resetpassword /></Suspense>} />
            <Route path="privacy-policy" element={<Suspense fallback={<LoadingSpinner />}><PrivacyPolicy /></Suspense>} />
            <Route path="refund-policy" element={<Suspense fallback={<LoadingSpinner />}><RefundPloicy /></Suspense>} />
            <Route path="shipping-policy" element={<Suspense fallback={<LoadingSpinner />}><ShippingPolicy /></Suspense>} />
            <Route path="term-conditions" element={<Suspense fallback={<LoadingSpinner />}><TermAndContions /></Suspense>} />
            <Route path="*" element={<Suspense fallback={<LoadingSpinner />}><NotFound /></Suspense>} />
          </Route>
        </Routes>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
